"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { updateCrud, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { Button } from '@open-mercato/ui/primitives/button'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { PARTICIPANT_PROFILE_ENTITY_ID, formatParticipantTypeLabel, formatSupportedModeLabel } from '../../../lib/participantProfile'
import {
  buildParticipantUpdatePayload,
  createParticipantFormFields,
  createParticipantFormGroups,
  createParticipantFormSchema,
  mapParticipantDetailToFormValues,
  type ParticipantFormValues,
} from '../../../components/formConfig'

type ParticipantDetailResponse = {
  participant: Record<string, unknown>
  linkedCustomer: {
    id: string
    displayName: string
    primaryEmail?: string | null
    primaryPhone?: string | null
    status?: string | null
    lifecycleStage?: string | null
  } | null
  customFields?: Record<string, unknown>
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export default function ParticipantDetailPage({ params }: { params?: { id?: string } }) {
  const participantId = params?.id ?? null
  const t = useT()
  const [detail, setDetail] = React.useState<ParticipantDetailResponse | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const formSchema = React.useMemo(() => createParticipantFormSchema(), [])
  const fields = React.useMemo(() => createParticipantFormFields(t), [t])
  const groups = React.useMemo(() => createParticipantFormGroups(t), [t])

  const loadDetail = React.useCallback(async () => {
    if (!participantId) {
      setDetail(null)
      setError(t('participants.detail.errors.missingId', 'Participant id is missing.'))
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const payload = await readApiResultOrThrow<ParticipantDetailResponse>(
        `/api/participants/${encodeURIComponent(participantId)}`,
        undefined,
        { errorMessage: t('participants.detail.loadError', 'Failed to load participant profile.') },
      )
      setDetail(payload)
    } catch (loadError) {
      const message = loadError instanceof Error && loadError.message
        ? loadError.message
        : t('participants.detail.loadError', 'Failed to load participant profile.')
      setError(message)
      setDetail(null)
    } finally {
      setIsLoading(false)
    }
  }, [participantId, t])

  React.useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  const contentHeader = React.useMemo(() => {
    const linkedCustomer = detail?.linkedCustomer ?? null
    const participant = detail?.participant ?? {}
    if (!linkedCustomer) return null
    const participantType = normalizeText(participant.participantType)
    const supportedModes = Array.isArray(participant.supportedModes)
      ? participant.supportedModes.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      : []
    return (
      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('participants.detail.crmLabel', 'Linked CRM company')}
            </div>
            <div className="text-base font-semibold">{linkedCustomer.displayName}</div>
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {linkedCustomer.primaryEmail ? <span>{linkedCustomer.primaryEmail}</span> : null}
              {linkedCustomer.primaryPhone ? <span>{linkedCustomer.primaryPhone}</span> : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {participantType ? <Badge variant="secondary">{formatParticipantTypeLabel(participantType)}</Badge> : null}
            {supportedModes.slice(0, 2).map((mode) => (
              <Badge key={mode} variant="outline">{formatSupportedModeLabel(mode)}</Badge>
            ))}
            <Button asChild variant="outline" size="sm">
              <Link href={`/backend/customers/companies/${linkedCustomer.id}`}>
                {t('participants.detail.actions.openCompany', 'Open company')}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }, [detail, t])

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('participants.detail.loading', 'Loading participant profile…')} />
        </PageBody>
      </Page>
    )
  }

  if (error || !detail || !participantId) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={error ?? t('participants.detail.notFound', 'Participant profile was not found.')} />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageBody>
        <CrudForm<ParticipantFormValues>
          title={t('participants.detail.title', 'Participant profile')}
          backHref="/backend/participants"
          cancelHref="/backend/participants"
          deleteRedirect="/backend/participants"
          submitLabel={t('participants.form.submitUpdate', 'Save changes')}
          schema={formSchema}
          fields={fields}
          groups={groups}
          entityId={PARTICIPANT_PROFILE_ENTITY_ID}
          initialValues={mapParticipantDetailToFormValues(detail)}
          contentHeader={contentHeader}
          onSubmit={async (values) => {
            await updateCrud('participants', buildParticipantUpdatePayload(participantId, values))
            flash(t('participants.form.successUpdate', 'Participant profile saved.'), 'success')
            await loadDetail()
          }}
          onDelete={async () => {
            await deleteCrud('participants', participantId)
          }}
        />
      </PageBody>
    </Page>
  )
}
